import Axios from "axios";
import createRestApiClient from "../../utility/createRestApiClient";
import { getCookieData } from "../../utility/cookieHandler/handler";
import {
  getApiBaseUrl,
  isSharingRoute
} from "../../utility/utilityFunctions";
import {
  CLEAR_CYPRESS_GROUPED_DATA,
  FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_FAILED,
  FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_LOADING,
  FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_SUCCESS,
  FETCH_CYPRESS_GROUPED_DATA_FAILED,
  FETCH_CYPRESS_GROUPED_DATA_LOADING,
  FETCH_CYPRESS_GROUPED_DATA_SUCCESS,
  SET_ACTIVE_ACCORDION_ON_TEST_LIST,
  UPDATE_CYPRESS_GROUPED_DATA,
  UPDATE_CYP_SIDEBAR_GROUPED_DATA,
  CLEAR_STATUS_WISE_CYPRESS_GROUPED_DATA,
  DELETE_CYPRESS_GROUPED_TEST
} from "../actionTypeConstants/cypressGroupedTestConstants";
import { DEFAULT_TEST_DATA_API_CALL_LIMIT } from "../../common/constants/View";
import { testStatus } from "../../utility/ml2.0/constants";
import cloneDeep from "lodash/cloneDeep";

const getQueryString = (filter, newPagination) => {
  let queryString = "";
  Object.keys(filter).forEach(k => {
    if (filter[k]) {
      if (filter[k] instanceof Array) {
        if (filter[k].length > 0) {
          if (k === "range" && !filter.filterBy) {
            queryString = `${queryString}&${k}=${filter[k].join(",")}`;
          } else if (k === "build" && !newPagination) {
            // Pass 'buildName' as the key, as we're storing the name of the build in redux store corresponding to build
            // and BE supports queryParam 'buildName' for the same.
            queryString = `${queryString}&buildName=${filter[k].join(",")}`;
          } else {
            queryString = `${queryString}&${k}=${filter[k].join(",")}`;
          }
        }
      }
      if (typeof filter[k] === "string" || filter[k] instanceof String) {
        if (filter[k].trim() !== "") {
          if (k === "build" && !newPagination) {
            // Pass 'buildName' as the key, as we're storing the name of the build in redux store corresponding to build
            // and BE supports queryParam 'buildName' for the same.
            queryString = `${queryString}&buildName=${filter[k]}`;
          } else {
            queryString = `${queryString}&${k}=${filter[k]}`;
          }
        }
      }
    }
  });
  return queryString;
};

const CancelToken = Axios.CancelToken;
let source;

export const fetchCypressTestGroup = (
  token,
  status = "",
  searchText = "",
  buildId,
  offset = 0,
  automationApiHost,
  fetchDataStatusWise = false,
  groupTestFilter = {},
  shareId
) => {
  source = CancelToken.source();
  let statusQueryParam = "";

  let groupFiltersToApply = { ...groupTestFilter };
  const groupedFilterStatusList = groupFiltersToApply.status;

  // status will be according to the accordion clicked
  if (fetchDataStatusWise) {
    delete groupFiltersToApply.status;
  }
  delete groupFiltersToApply.build;
  delete groupFiltersToApply.searchText;

  let filterToApply = cloneDeep(groupFiltersToApply);

  let browserFilter = filterToApply["browser"];
  if (browserFilter && browserFilter?.length > 0) {
    const isEdgeAppliedIndex = browserFilter.findIndex(name => name === "edge");
    if (isEdgeAppliedIndex > -1) {
      filterToApply["browser"][isEdgeAppliedIndex] = "MicrosoftEdge";
    }
  }

  const groupFilterQueryParam = getQueryString(filterToApply);

  if (status === "other") {
    const statusToMap =
      groupedFilterStatusList && groupedFilterStatusList.length > 0
        ? groupedFilterStatusList
        : testStatus.map(status => status.value);

    const otherStatus = statusToMap.filter(status => {
      if (status !== "passed" && status !== "failed" && status !== "running") {
        return status;
      }
    });
    statusQueryParam = getQueryString({ status: otherStatus });
  } else {
    statusQueryParam = status ? `&status=${status}` : "";
  }

  let queryString;

  if (isSharingRoute() && shareId) {
    queryString = `searchText=${searchText}${groupFilterQueryParam}&shareId=${shareId}`;
  } else {
    queryString = `searchText=${searchText}&build=${buildId}${statusQueryParam}${groupFilterQueryParam}`;
  }

  const mockApiEndpoint = automationApiHost || getApiBaseUrl();
  const mockApiClient = createRestApiClient().withConfig({
    baseURL: mockApiEndpoint
  }).client;

  const cookieName = process.env.REACT_APP_COOKIE_NAME;
  const accessToken = getCookieData(cookieName) || token;
  if (!accessToken && !isSharingRoute()) {
    window.location.href =
      process.env.REACT_APP_LOGIN_URL || window?._env_?.REACT_APP_LOGIN_URL;
  }
  if (
    queryString.includes("searchText=") &&
    !queryString.includes("searchText=testId:") &&
    !queryString.includes("searchText=sessionId:")
  ) {
    queryString = queryString.replace("searchText=", "searchText=testName:");
  }

  let header =
    isSharingRoute && shareId
      ? {
          "x-ml-auth": shareId,
          "x-ml-user": "sharing-client"
        }
      : { Authorization: `Bearer ` + accessToken };

  const timezoneOffset = getCookieData("timezoneOffset");
  let endPoint = `/api/v2/test?timezone=${timezoneOffset}&offset=${offset}&limit=${DEFAULT_TEST_DATA_API_CALL_LIMIT}&${queryString}&includeCreated=0&caps=1`;

  // Only fetch the total count incase user is on first page i.e first load.
  if (offset === 0) {
    endPoint = endPoint + `&countEnable=true`;
  }

  let typesArray = [];

  if (fetchDataStatusWise) {
    typesArray = [
      FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_LOADING,
      FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_SUCCESS,
      FETCH_STATUS_WISE_CYPRESS_GROUPED_DATA_FAILED
    ];
  } else {
    typesArray = [
      FETCH_CYPRESS_GROUPED_DATA_LOADING,
      FETCH_CYPRESS_GROUPED_DATA_SUCCESS,
      FETCH_CYPRESS_GROUPED_DATA_FAILED
    ];
  }

  return {
    payload: {
      offset,
      status
    },
    types: [...typesArray],
    promise: () =>
      mockApiClient.get(endPoint, {
        headers: header,
        cancelToken: source.token
      })
  };
};

export const setActiveAccordionForCypress = (
  activeAccordion = null,
  statusCount = {}
) => {
  return {
    type: SET_ACTIVE_ACCORDION_ON_TEST_LIST,
    payload: { activeAccordion, statusCount }
  };
};

export const updateCypressGroupedData = testList => {
  return {
    type: UPDATE_CYPRESS_GROUPED_DATA,
    payload: testList
  };
};

export const updateCypSidebarGroupedData = testList => {
  return {
    type: UPDATE_CYP_SIDEBAR_GROUPED_DATA,
    payload: testList
  };
};

export const clearCypressGroupedData = (isTestListPage = false) => {
  const type = isTestListPage
    ? CLEAR_STATUS_WISE_CYPRESS_GROUPED_DATA
    : CLEAR_CYPRESS_GROUPED_DATA;

  return {
    type
  };
};

export function cancelCypressGroupRequest() {
  if (source) {
    //Can we move api request cancelled to a constant and use it at all places.
    source.cancel("api request cancelled");
  }
  return dispatch =>
    dispatch({
      type: CLEAR_CYPRESS_GROUPED_DATA
    });
}

export const deleteCypressGroupedTest = id => {
  return {
    type: DELETE_CYPRESS_GROUPED_TEST,
    payload: id
  };
};
